import React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { control } from '../tokens';
import cn from './cn';

/**
 * The tick, or the bar of a mixed box: white, filling the box inside its 1px edge. Stroke 3
 * on Lucide's 24-unit grid is 2px at this size, the weight the design draws the tick at.
 *
 * Exported so MUI's checkbox, which `store/rootComponents/themeControls.js` restyles to
 * match this one, draws exactly the same glyph rather than its own.
 */
export const CheckboxMark = ({ mixed = false }) => {
  const Glyph = mixed ? Minus : Check;
  return <Glyph size={control.checkSize - 2} strokeWidth={3} aria-hidden="true" />;
};

/**
 * Radix Checkbox.
 *
 * Takes `checked` / `onChange(boolean)` rather than Radix's `onCheckedChange`, matching
 * the shape the CMS form components already use. `checked="indeterminate"` renders the
 * mixed state and sets `aria-checked="mixed"`, which the hand-rolled tri-state checkboxes
 * in the audience selector never did.
 *
 * Redesign iteration 1 (2026-09-29): one checkbox everywhere. An 18px box with a 4px radius
 * and the edge every input has (`--input-border`); `--check-on` with a white tick when
 * checked, or a white bar when mixed. Disabled is `--fill` inside a `--border-light` edge,
 * and disabled and checked is a solid neutral box, so a choice that cannot be changed still
 * reads as made. Hover darkens the edge. The disabled rules come after hover in Tailwind's
 * output and the checked ones after both, so hover never shows through either state.
 */
const Checkbox = React.forwardRef(
  ({ checked = false, onChange, disabled = false, className, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onChange && onChange(next)}
      className={cn(
        'tw-inline-flex tw-h-check tw-w-check tw-shrink-0 tw-items-center tw-justify-center',
        'tw-rounded-check tw-border tw-border-solid tw-border-input-border tw-bg-white tw-p-0',
        'tw-cursor-pointer tw-transition-colors tw-duration-150',
        'hover:tw-border-muted',
        'data-[state=checked]:tw-border-check-on data-[state=checked]:tw-bg-check-on',
        'data-[state=indeterminate]:tw-border-check-on data-[state=indeterminate]:tw-bg-check-on',
        'disabled:tw-cursor-not-allowed disabled:tw-border-line-light disabled:tw-bg-fill',
        'disabled:data-[state=checked]:tw-border-neutral-500',
        'disabled:data-[state=checked]:tw-bg-neutral-500',
        'disabled:data-[state=indeterminate]:tw-border-neutral-500',
        'disabled:data-[state=indeterminate]:tw-bg-neutral-500',
        'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2',
        'focus-visible:tw-outline-brand-focus',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="tw-flex tw-items-center tw-justify-center tw-text-white">
        <CheckboxMark mixed={checked === 'indeterminate'} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
);
Checkbox.displayName = 'Checkbox';

export default Checkbox;
